import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { withTranslation } from "react-i18next";
import axios from "axios";
import MDSpinner from "react-md-spinner";
import {
  FaClipboardList,
  FaCalendarAlt,
  FaInbox,
  FaPlus,
  FaCheckCircle,
  FaFilter,
  FaSortAmountDown,
  FaSortAmountUp,
} from "react-icons/fa";

import { usePendingSchedules } from "../../contexts/PendingSchedulesContext";
import ScheduledBadge, {
  getObsCardClass,
  ObservationExecutionInfo,
} from "../Operation/ScheduledBadge";
import { hasExecutedPeriod } from "../../helpers/observationDateUtils";
import myConfig from "../../configs/config";
import CreateScheduleModal from "./CreateScheduleModal";
import ServiceOrderInfoButton from "./ServiceOrderInfoButton";
import FieldAppHarvestLossOrderResults from "../FieldApp/FieldAppHarvestLossOrderResults";
import FieldAppPlantingCalibrationOrderResults from "../FieldApp/FieldAppPlantingCalibrationOrderResults";
import {
  serviceOrderTypeLabel,
  showsFertilizationFields,
  showsHarvestLossFields,
  showsPlantingFields,
} from "../../constants/serviceOrderTypes";
import { sortObservationsByRecency } from "./serviceOrderListSort";
import "./PendingSchedules.css";
import "../FieldApp/FieldAppHarvestLossOrderResults.css";
import "../Operation/Operation.css";

function ServiceOrderHarvestLossResults({
  farmId,
  obs,
  t,
  defaultExpanded = false,
}) {
  if (!showsHarvestLossFields(obs.service_type)) return null;

  return (
    <FieldAppHarvestLossOrderResults
      farmId={farmId}
      observationId={obs.id}
      serviceOrderPlan={obs.service_order_plan}
      serviceOrderPlanPublished={obs.service_order_plan_published}
      orderLabel={
        obs.operationName
          ? `${obs.operationName} (#${obs.id})`
          : `#${obs.id}`
      }
      selection={{}}
      defaultExpanded={defaultExpanded}
      t={t}
    />
  );
}

function ServiceOrderPlantingResults({
  farmId,
  obs,
  t,
  defaultExpanded = false,
}) {
  const isPlanting = showsPlantingFields(obs.service_type);
  const isFertilization = showsFertilizationFields(obs.service_type);
  if (!isPlanting && !isFertilization) return null;

  return (
    <FieldAppPlantingCalibrationOrderResults
      farmId={farmId}
      observationId={obs.id}
      variant={isFertilization ? "fertilization" : "planting"}
      orderLabel={
        obs.operationName
          ? `${obs.operationName} (#${obs.id})`
          : `#${obs.id}`
      }
      selection={{}}
      defaultExpanded={defaultExpanded}
      t={t}
    />
  );
}

function PendingSchedules({ t }) {
  const { farm_id } = useParams();
  const location = useLocation();
  const {
    pendingObs,
    completedObs,
    loading,
    completedLoading,
    error,
    completedError,
    refreshPending,
    refreshCompleted,
  } = usePendingSchedules();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [activeTab, setActiveTab] = useState("pending");
  const [newestFirst, setNewestFirst] = useState(true);

  const focusObsId = useMemo(() => {
    const raw = new URLSearchParams(location.search).get("obs");
    if (!raw) return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  }, [location.search]);

  const expandHarvestLoss = useMemo(
    () => new URLSearchParams(location.search).get("expand_loss") === "1",
    [location.search]
  );
  const expandPlanting = useMemo(
    () =>
      new URLSearchParams(location.search).get("expand_plantio") === "1" ||
      new URLSearchParams(location.search).get("expand_fertilizacao") === "1",
    [location.search]
  );

  useEffect(() => {
    document.title = "Agriconnected - Ordens de Serviço";
  }, []);

  useEffect(() => {
    if (!focusObsId) return;
    const inCompleted = completedObs.some(
      (obs) => Number(obs.id) === focusObsId
    );
    const inPending = pendingObs.some((obs) => Number(obs.id) === focusObsId);
    if (inCompleted) {
      setActiveTab("completed");
    } else if (inPending) {
      setActiveTab("pending");
    }
  }, [focusObsId, completedObs, pendingObs]);

  useEffect(() => {
    if (!focusObsId) return undefined;
    if (loading || (activeTab === "completed" && completedLoading)) {
      return undefined;
    }
    const timer = window.setTimeout(() => {
      const element = document.getElementById(
        `pending-schedules-obs-${focusObsId}`
      );
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 320);
    return () => window.clearTimeout(timer);
  }, [
    focusObsId,
    loading,
    completedLoading,
    activeTab,
    pendingObs,
    completedObs,
  ]);

  const sortedPendingObs = sortObservationsByRecency(pendingObs, newestFirst);
  const sortedCompletedObs = sortObservationsByRecency(
    completedObs,
    newestFirst
  );

  const handleConfirm = useCallback(
    async (obsId) => {
      try {
        await axios.patch(
          `${myConfig.API_URL}/observation/${obsId}/`,
          { confirmed: true },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("client-token")}`,
            },
          }
        );
        refreshPending();
      } catch (err) {
        console.error("Erro ao confirmar observação:", err);
      }
    },
    [refreshPending]
  );

  const isLoading =
    activeTab === "pending"
      ? loading
      : completedLoading && completedObs.length === 0;

  if (isLoading) {
    return (
      <div className="pending-schedules-page">
        <div style={{ textAlign: "center", paddingTop: "60px" }}>
          <MDSpinner size={48} />
        </div>
      </div>
    );
  }

  const tabError = activeTab === "pending" ? error : completedError;

  return (
    <div className="pending-schedules-page">
      <div className="pending-schedules-header">
        <div className="pending-schedules-header__main">
          <FaClipboardList className="pending-schedules-header__icon" />
          <h3 className="pending-schedules-header__title">
            {t("Ordens de Serviço")}
          </h3>
          {activeTab === "pending" && pendingObs.length > 0 && (
            <span className="pending-schedules-total-badge">
              {pendingObs.length}
            </span>
          )}
          {activeTab === "completed" && completedObs.length > 0 && (
            <span className="pending-schedules-total-badge pending-schedules-total-badge--completed">
              {completedObs.length}
            </span>
          )}
        </div>
        <div className="pending-schedules-create-actions">
          <ServiceOrderInfoButton t={t} />
          <button
            type="button"
            className="btn btn-success pending-schedules-create-btn"
            onClick={() => setShowCreateModal(true)}
          >
            <FaPlus style={{ marginRight: 6 }} />
            {t("Nova Ordem de Serviço")}
          </button>
        </div>
      </div>

      <div className="pending-schedules-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "pending"}
          className={`pending-schedules-tab${
            activeTab === "pending" ? " pending-schedules-tab--active" : ""
          }`}
          onClick={() => setActiveTab("pending")}
        >
          <FaCalendarAlt style={{ marginRight: 6 }} />
          {t("Pendentes")} ({pendingObs.length})
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "completed"}
          className={`pending-schedules-tab${
            activeTab === "completed" ? " pending-schedules-tab--active" : ""
          }`}
          onClick={() => setActiveTab("completed")}
        >
          <FaCheckCircle style={{ marginRight: 6 }} />
          {t("Concluídas")} ({completedObs.length})
        </button>
        <button
          type="button"
          className="pending-schedules-sort-btn"
          onClick={() => setNewestFirst((current) => !current)}
          title={
            newestFirst
              ? t("Mais recentes em cima. Clique para inverter.")
              : t("Mais antigas em cima. Clique para inverter.")
          }
        >
          {newestFirst ? (
            <FaSortAmountDown style={{ marginRight: 6 }} />
          ) : (
            <FaSortAmountUp style={{ marginRight: 6 }} />
          )}
          {newestFirst ? t("Mais recentes") : t("Mais antigas")}
        </button>
      </div>

      {tabError && (
        <div className="alert alert-danger pending-schedules-error">
          {t("Erro ao carregar ordens de serviço.")}{" "}
          <button
            type="button"
            className="btn btn-link btn-sm"
            onClick={
              activeTab === "pending" ? refreshPending : refreshCompleted
            }
          >
            {t("Tentar novamente")}
          </button>
        </div>
      )}

      <CreateScheduleModal
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
        farmId={farm_id}
        onCreated={refreshPending}
        t={t}
      />

      {activeTab === "pending" && !tabError && pendingObs.length === 0 ? (
        <div className="pending-schedules-empty">
          <FaInbox className="pending-schedules-empty__icon" />
          <p>{t("Nenhuma ordem de serviço pendente.")}</p>
        </div>
      ) : activeTab === "pending" && !tabError ? (
        <>
          <section>
            {sortedPendingObs.map((obs) => (
              <PendingObsCard
                key={obs.id}
                obs={obs}
                farm_id={farm_id}
                onConfirm={handleConfirm}
                focusObsId={focusObsId}
                expandHarvestLoss={expandHarvestLoss}
                expandPlanting={expandPlanting}
                t={t}
              />
            ))}
          </section>
        </>
      ) : null}

      {activeTab === "completed" && !tabError && completedObs.length === 0 ? (
        <div className="pending-schedules-empty">
          <FaCheckCircle className="pending-schedules-empty__icon" />
          <p>{t("Nenhuma ordem de serviço concluída ainda.")}</p>
        </div>
      ) : activeTab === "completed" && !tabError ? (
        <section>
          <div className="pending-schedules-group-header pending-schedules-group-header--completed">
            <FaCheckCircle style={{ marginRight: "6px" }} />
            {t("Serviços executados")} ({completedObs.length})
          </div>
          {sortedCompletedObs.map((obs) => (
            <CompletedObsCard
              key={obs.id}
              obs={obs}
              farm_id={farm_id}
              focusObsId={focusObsId}
              expandHarvestLoss={expandHarvestLoss}
              expandPlanting={expandPlanting}
              t={t}
            />
          ))}
        </section>
      ) : null}
    </div>
  );
}

function ServiceOrderLocationInfo({ obs, t }) {
  const farmLabel = obs.service_farm_names || obs.site_farm_name;
  if (!farmLabel && !obs.land_plot_names && !obs.audio_message_url) return null;
  return (
    <div className="pending-schedules-card__location">
      {farmLabel ? (
        <span>
          <strong>{t("Fazenda")}:</strong> {farmLabel}
        </span>
      ) : null}
      {obs.land_plot_names ? (
        <span>
          <strong>{t("Talhões")}:</strong> {obs.land_plot_names}
        </span>
      ) : null}
      {obs.track_heading_bearing != null ? (
        <span>
          <strong>{t("Rumo A→B")}:</strong>{" "}
          {Number(obs.track_heading_bearing).toFixed(2)}°
        </span>
      ) : null}
      {obs.track_heading_point_a_lat != null &&
      obs.track_heading_point_a_lng != null ? (
        <span className="pending-schedules-card__track-heading-coords">
          <strong>{t("Ponto A")}:</strong>{" "}
          {Number(obs.track_heading_point_a_lat).toFixed(6)},{" "}
          {Number(obs.track_heading_point_a_lng).toFixed(6)}
        </span>
      ) : null}
      {obs.track_heading_point_b_lat != null &&
      obs.track_heading_point_b_lng != null ? (
        <span className="pending-schedules-card__track-heading-coords">
          <strong>{t("Ponto B")}:</strong>{" "}
          {Number(obs.track_heading_point_b_lat).toFixed(6)},{" "}
          {Number(obs.track_heading_point_b_lng).toFixed(6)}
        </span>
      ) : null}
      {obs.track_heading_on_farm_map ? (
        <span>
          <strong>{t("Mapa da fazenda")}:</strong> {t("Rumo A-B publicado")}
        </span>
      ) : null}
      {obs.track_heading_summary ? (
        <span className="pending-schedules-card__track-heading">
          <strong>{t("Rumo de pista")}:</strong>{" "}
          {obs.track_heading_summary.split("\n")[0]}
        </span>
      ) : null}
      {obs.audio_message_url ? (
        <div className="pending-schedules-card__audio">
          <strong>{t("Áudio")}:</strong>
          <audio controls preload="metadata" src={obs.audio_message_url} />
        </div>
      ) : null}
      {obs.track_heading_audio_url ? (
        <div className="pending-schedules-card__audio">
          <strong>{t("Áudio do rumo")}:</strong>
          <audio controls preload="metadata" src={obs.track_heading_audio_url} />
        </div>
      ) : null}
    </div>
  );
}

function PendingObsCard({
  obs,
  farm_id,
  onConfirm,
  focusObsId,
  expandHarvestLoss,
  expandPlanting,
  t,
}) {
  const title = obs.operationName || obs.description || t("Ordem de serviço");
  const isFocused =
    focusObsId != null && Number(obs.id) === Number(focusObsId);

  return (
    <div
      id={`pending-schedules-obs-${obs.id}`}
      className={`${getObsCardClass(obs)} pending-schedules-card${
        isFocused ? " pending-schedules-card--focused" : ""
      }`}
    >
      {obs.operationId ? (
        <Link
          to={`/${farm_id}/operation/${obs.operationId}`}
          className="pending-schedules-operation-link"
        >
          {title}
        </Link>
      ) : (
        <div className="pending-schedules-operation-link pending-schedules-operation-link--static">
          {title}
        </div>
      )}
      {obs.service_type ? (
        <span className="pending-schedules-type-badge">
          {serviceOrderTypeLabel(obs.service_type, t)}
        </span>
      ) : null}
      <ServiceOrderLocationInfo obs={obs} t={t} />
      <ObservationExecutionInfo obs={obs} t={t} compact />
      {obs.description && obs.operationId && (
        <div className="pending-schedules-card__description">
          {obs.description}
        </div>
      )}
      {obs.products_used && (
        <div className="pending-schedules-card__products">
          <strong>{t("Produtos")}:</strong> {obs.products_used}
        </div>
      )}
      {Array.isArray(obs.assigned_operators) &&
        obs.assigned_operators.length > 0 && (
          <div className="pending-schedules-card__operators">
            {t("Enviado para")}:{" "}
            {obs.assigned_operators.map((operator) => operator.name).join(", ")}
          </div>
        )}
      {obs.visible_at && (
        <div className="pending-schedules-card__visible-at">
          {t("Visível no app em")}:{" "}
          {new Date(obs.visible_at).toLocaleString("pt-BR")}
        </div>
      )}
      <ServiceOrderPlantingResults
        farmId={farm_id}
        obs={obs}
        t={t}
        defaultExpanded={isFocused && expandPlanting}
      />
      <ServiceOrderHarvestLossResults
        farmId={farm_id}
        obs={obs}
        t={t}
        defaultExpanded={isFocused && expandHarvestLoss}
      />
      <ScheduledBadge obs={obs} onConfirm={onConfirm} />
    </div>
  );
}

function CompletedObsCard({
  obs,
  farm_id,
  focusObsId,
  expandHarvestLoss,
  expandPlanting,
  t,
}) {
  const title = obs.operationName || obs.description || t("Ordem de serviço");
  const trackingHint = hasExecutedPeriod(obs)
    ? t("Ver operação e rastreamento do período executado")
    : t("Ver operação e rastreamento");
  const isFocused =
    focusObsId != null && Number(obs.id) === Number(focusObsId);

  return (
    <div
      id={`pending-schedules-obs-${obs.id}`}
      className={`${getObsCardClass(obs)} pending-schedules-card${
        isFocused ? " pending-schedules-card--focused" : ""
      }`}
    >
      {obs.operationId ? (
        <Link
          to={`/${farm_id}/operation/${obs.operationId}`}
          className="pending-schedules-operation-link"
        >
          {title}
        </Link>
      ) : (
        <div className="pending-schedules-operation-link pending-schedules-operation-link--static">
          {title}
        </div>
      )}
      {obs.service_type ? (
        <span className="pending-schedules-type-badge">
          {serviceOrderTypeLabel(obs.service_type, t)}
        </span>
      ) : null}
      <ServiceOrderLocationInfo obs={obs} t={t} />
      <ObservationExecutionInfo obs={obs} t={t} compact />
      {obs.description && obs.operationId && (
        <div className="pending-schedules-card__description">
          {obs.description}
        </div>
      )}
      {obs.products_used && (
        <div className="pending-schedules-card__products">
          <strong>{t("Produtos")}:</strong> {obs.products_used}
        </div>
      )}
      {obs.operationId && (
        <Link
          to={`/${farm_id}/operation/${obs.operationId}?filter_obs=${obs.id}`}
          className="btn btn-sm btn-outline-success pending-schedules-tracking-btn"
          title={trackingHint}
        >
          <FaFilter style={{ marginRight: 6 }} />
          {trackingHint}
        </Link>
      )}
      <ServiceOrderPlantingResults
        farmId={farm_id}
        obs={obs}
        t={t}
        defaultExpanded={isFocused && expandPlanting}
      />
      <ServiceOrderHarvestLossResults
        farmId={farm_id}
        obs={obs}
        t={t}
        defaultExpanded={isFocused && expandHarvestLoss}
      />
    </div>
  );
}

export default withTranslation()(PendingSchedules);
